import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, MessageSquare, Image as ImageIcon, Brain, Shield, Infinity as InfinityIcon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { noTextSelect } from '@/lib/utils';
import { useTypewriter } from '@/hooks/useTypewriter';
import { useSupabase } from '@/hooks/useSupabase';
import { getDailyQuotaStatus } from '@/services/dailyQuotaService';

export const PricingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user: authUser } = useSupabase();
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
  const [planTier, setPlanTier] = useState<string>('free');

  // Fetch user's actual plan tier
  useEffect(() => {
    const fetchPlanTier = async () => {
      if (!authUser) {
        setPlanTier('free');
        return;
      }
      try {
        const status = await getDailyQuotaStatus();
        setPlanTier((status?.tier || 'free').toString());
      } catch {
        setPlanTier('free');
      }
    };
    fetchPlanTier();
  }, [authUser]);

  const plans = useMemo(
    () => [
      {
        key: 'pro',
        title: 'Pro',
        special: 'SPECIAL OFFER',
        monthlyPrice: '399',
        annualPrice: '3990',
        oldMonthlyPrice: '999',
        oldAnnualPrice: '8999',
        tagline: 'Do more with smarter AI',
        buttonText: 'Upgrade to Pro',
        buttonVariant: 'primary' as const,
        features: [
          { icon: Sparkles, text: 'Go deep on harder questions' },
          { icon: MessageSquare, text: 'Chat longer and upload more content' },
          { icon: ImageIcon, text: 'Make realistic images for your projects' },
          { icon: Brain, text: 'Store more context for smarter replies' },
          { icon: Shield, text: 'Get help with planning and tasks' },
          { icon: InfinityIcon, text: 'Explore projects, tasks, and custom GPTs' },
        ],
        footnoteTitle: 'Promo terms apply.',
        footnote:
          billingPeriod === 'monthly'
            ? 'Special pricing for limited time. Cancel anytime.'
            : 'Save 2 months with annual billing. Cancel anytime.',
      },
      {
        key: 'free',
        title: 'Free',
        monthlyPrice: '0',
        annualPrice: '0',
        tagline: 'See what AI can do',
        buttonText: 'Your current plan',
        buttonVariant: 'muted' as const,
        features: [
          { icon: Sparkles, text: 'Get simple explanations' },
          { icon: MessageSquare, text: 'Have short chats for common questions' },
          { icon: ImageIcon, text: 'Try out limited image generation' },
          { icon: Brain, text: 'Save limited memory and context' },
        ],
      },
    ],
    [billingPeriod]
  );

  const pageBg = isDarkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const proTaglineTyped = useTypewriter('Do more with smarter AI', { speedMs: 40, startDelayMs: 140 });

  return (
    <div className={`min-h-screen ${pageBg} ${textPrimary}`}>
      <div className="max-w-7xl mx-auto px-2 xspx-4 sm:px-6 lg:px-8 py-6">
        <div className="relative flex items-center justify-center mb-6">
          <h1 className="text-xl sm:text-2xl font-medium">Choose Your Plan</h1>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={noTextSelect(
              `absolute right-0 inline-flex items-center justify-center rounded-full w-10 h-10 transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-200' : 'hover:bg-black/5 text-gray-700'
              }`
            )}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className={`inline-flex rounded-full p-1 ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-gray-200 border border-gray-300'}`}>
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className={noTextSelect(
                `px-6 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'monthly'
                  ? isDarkMode
                    ? 'bg-white text-black'
                    : 'bg-white text-black shadow-sm'
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annually')}
              className={noTextSelect(
                `px-6 py-2 rounded-full text-sm font-medium transition-all ${billingPeriod === 'annually'
                  ? isDarkMode
                    ? 'bg-white text-black'
                    : 'bg-white text-black shadow-sm'
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`
              )}
            >
              Annually
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 items-stretch max-w-[830px] mx-auto justify-items-center">
          {plans.map((plan) => {
            const isGo = plan.key === 'pro';
            const cardBg = isGo
              ? isDarkMode
                ? 'bg-[#26284a] border-[#3a3d74]'
                : 'bg-[#F5F5FF] border-[#6a67ff]'
              : isDarkMode
                ? 'bg-[#1a1a1b] border-[#2a2a2b]'
                : 'bg-white border-gray-200';

            const currentPrice = billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
            const oldPrice = billingPeriod === 'monthly' ? plan.oldMonthlyPrice : plan.oldAnnualPrice;
            const priceSuffix = billingPeriod === 'monthly' ? 'INR / month' : 'INR / year';

            const normalizedTier = planTier.toLowerCase();
            const isPro = normalizedTier === 'pro' || normalizedTier === 'enterprise' || normalizedTier === 'starter';
            const isCurrent = (plan.key === 'free' && !isPro) || (plan.key === 'pro' && isPro);
            const dynamicButtonText = isCurrent ? 'Your current plan' : (plan.key === 'pro' ? 'Upgrade to Pro' : 'Free plan');

            const buttonClasses = (() => {
              if (isCurrent || plan.key === 'free') {
                return isDarkMode
                  ? 'bg-white/10 text-gray-200 border border-white/10 cursor-default'
                  : 'bg-gray-100 text-gray-600 border border-gray-200 cursor-default';
              }
              if (plan.buttonVariant === 'primary') {
                return 'bg-[#6a67ff] hover:bg-[#5b58ff] text-white';
              }
              return isDarkMode
                ? 'bg-white text-black hover:bg-white/90'
                : 'bg-black text-white hover:bg-black/90';
            })();

            return (
              <div
                key={plan.key}
                className={`w-full max-w-[400px] rounded-2xl border ${cardBg} p-5 sm:p-6 flex flex-col min-h-[500px]`}
              >
                <div className="flex items-start justify-between">
                  <h2 className="text-lg sm:text-xl font-semibold">{plan.title}</h2>
                  {plan.special && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-white/10 border border-white/10 text-gray-100 tracking-wide">
                      {plan.special}
                    </span>
                  )}
                </div>

                <div className="mt-4">
                  <div className="flex items-end gap-2 flex-wrap">
                    {oldPrice && (
                      <span className="text-2xl sm:text-3xl line-through text-gray-300/70">{oldPrice}</span>
                    )}
                    <span className="text-4xl sm:text-5xl font-semibold leading-none">{currentPrice}</span>
                    <span className={`text-xs sm:text-sm mb-1 ${textMuted}`}>{priceSuffix}</span>
                  </div>
                  <p className={`mt-4 font-semibold ${textPrimary}`}>{isGo ? proTaglineTyped : plan.tagline}</p>
                </div>

                <button
                  type="button"
                  className={noTextSelect(`mt-4 w-full h-11 rounded-full text-sm font-semibold transition-colors ${buttonClasses}`)}
                  disabled={isCurrent || plan.key === 'free'}
                >
                  {dynamicButtonText}
                </button>

                <div className="mt-5 space-y-3">
                  {plan.features.map((f, idx) => {
                    const Icon = f.icon;
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className={`mt-0.5 rounded-md p-1 ${textMuted}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className={`${textPrimary} text-sm leading-snug`}>{f.text}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-auto hidden pt-6">
                  {plan.footnoteTitle && (
                    <div className={`text-xs ${textPrimary}`}>
                      <span className="underline font-semibold"></span>{' '}
                      <span className="opacity-90"></span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <div className={`${textMuted} text-xs`}></div>
          <div className={`${textMuted} text-xs underline`}></div>
        </div>
      </div>
    </div>
  );
};
